import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Users, HardHat, Briefcase, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Liste les personnes (salariés + sous-traitants) ayant travaillé sur la commande,
 * avec total jours, total heures et détail dépliable par date.
 */
export const OrderWorkersSection = ({ orderId }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    if (!orderId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const { data: res } = await api.getOrderWorkers(orderId);
        if (!cancelled) setData(res);
      } catch (_e) {
        if (!cancelled) setData({ workers: [], summary: { total_workers: 0, total_days: 0, total_hours: 0 } });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orderId]);

  const toggle = (key) => setExpanded((p) => ({ ...p, [key]: !p[key] }));

  if (loading) {
    return (
      <Card data-testid="order-workers-section">
        <CardContent className="py-6 flex items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const workers = data?.workers || [];
  const summary = data?.summary || { total_workers: 0, total_days: 0, total_hours: 0, employees_count: 0, subcontractors_count: 0 };

  return (
    <Card data-testid="order-workers-section">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-heading flex items-center gap-2">
          <Users className="h-4 w-4" /> Personnes ayant travaillé
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Summary line */}
        <div className="flex flex-wrap gap-2 text-sm" data-testid="order-workers-summary">
          <Badge variant="outline" className="bg-slate-50">
            Total : <strong className="ml-1">{summary.total_days}</strong> jours · {summary.total_hours} h
          </Badge>
          {summary.employees_count > 0 && (
            <Badge variant="outline" className="bg-blue-50 border-blue-200 text-blue-800">
              <HardHat className="h-3 w-3 mr-1" /> {summary.employees_count} salarié(s)
            </Badge>
          )}
          {summary.subcontractors_count > 0 && (
            <Badge variant="outline" className="bg-purple-50 border-purple-200 text-purple-800">
              <Briefcase className="h-3 w-3 mr-1" /> {summary.subcontractors_count} sous-traitant(s)
            </Badge>
          )}
        </div>

        {workers.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="order-workers-empty">
            Personne n'a encore travaillé sur cette commande.
          </p>
        ) : (
          <div className="border rounded-md divide-y">
            {workers.map((w) => {
              const key = `${w.kind}-${w.id}`;
              const open = !!expanded[key];
              const isEmp = w.kind === "employee";
              return (
                <div key={key} data-testid={`order-worker-${w.kind}-${w.id}`}>
                  <button
                    type="button"
                    className="w-full flex items-center gap-2 p-2 hover:bg-muted/50 text-left"
                    onClick={() => toggle(key)}
                  >
                    {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    {isEmp
                      ? <HardHat className="h-4 w-4 text-blue-600" />
                      : <Briefcase className="h-4 w-4 text-purple-600" />}
                    <span className="flex-1 text-sm font-medium">{w.name}</span>
                    <Badge variant="outline" className={isEmp ? "bg-blue-50 border-blue-200" : "bg-purple-50 border-purple-200"}>
                      {isEmp ? "Salarié" : "Sous-traitant"}
                    </Badge>
                    <span className="text-sm tabular-nums" data-testid={`order-worker-days-${w.id}`}>
                      <strong>{w.total_days}</strong>&nbsp;j · {w.total_hours}&nbsp;h
                    </span>
                  </button>
                  {open && w.dates && w.dates.length > 0 && (
                    <div className="px-3 py-2 bg-muted/30 text-xs space-y-1">
                      {w.dates.map((d) => (
                        <div key={d.date} className="flex justify-between gap-3">
                          <span className="text-muted-foreground tabular-nums">{d.date}</span>
                          <span className="tabular-nums">{d.days} j · {d.hours} h</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default OrderWorkersSection;
